import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase.js';
import { trackEvent } from '../lib/analytics.js';
import AuthHeader from './AuthHeader.jsx';
import SEO from '../components/SEO.jsx';
import PublicFonts from '../components/PublicFonts.jsx';
import './landing.css';

// Vendor signup flow:
// 1. Call the signUpVendor Cloud Function (creates Auth user + vendor + claims).
// 2. Sign in with the same credentials so the client picks up the new tokens + claims.
// 3. Navigate to / which routes by role.

// Countries we have phone-normalization support for. Keep in sync with
// functions/src/phone-helpers.js COUNTRY_CALLING_CODES.
const SUPPORTED_COUNTRIES = [
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BR', name: 'Brazil' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CL', name: 'Chile' },
  { code: 'DE', name: 'Germany' },
  { code: 'DK', name: 'Denmark' },
  { code: 'ES', name: 'Spain' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IL', name: 'Israel' },
  { code: 'IN', name: 'India' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SG', name: 'Singapore' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'ZA', name: 'South Africa' },
];

// Guess from browser locale; fall back to AU.
function guessCountry() {
  try {
    const locale = (navigator.language || 'en-AU');
    const region = locale.split('-')[1];
    if (region && SUPPORTED_COUNTRIES.some((c) => c.code === region.toUpperCase())) {
      return region.toUpperCase();
    }
  } catch { /* fall through */ }
  return 'AU';
}

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState(guessCountry);
  // Task #106 — optional AI add-on chosen at signup. Defaults to OFF (no extra
  // charge surprise). Admin can flip it on/off later in Settings.
  const [aiAddonEnabled, setAiAddonEnabled] = useState(false);
  // Operating mode (decided upfront). 'service' = vendor with external customers;
  // 'internal' = our own staff report breakdowns. Drives People-page labels +
  // sensible defaults. Editable later from Settings.
  const [operatingMode, setOperatingMode] = useState('service');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const signUpVendor = httpsCallable(functions, 'signUpVendor');
      await signUpVendor({
        email: email.trim().toLowerCase(),
        password,
        fullName: fullName.trim(),
        companyName: companyName.trim(),
        country,
        aiAddonEnabled,
        operatingMode,
      });
      // GA4 conversion: a new vendor account was created. This is the key event
      // that turns analytics into a funnel (visitors → signups). No-op if GA off.
      trackEvent('sign_up', { method: 'email', operating_mode: operatingMode });
      // Sign the new user in so we get a fresh ID token with the claims.
      await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Signup failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lp3 lp3-auth auth-shell">
      <SEO title="Get early access" noIndex />
      <PublicFonts />
      <form className="auth-card" onSubmit={onSubmit}>
        <AuthHeader />
        <h1>Create your vendor account</h1>
        <p className="subtitle">Set up Giraops for your service business. All fields required.</p>

        <label htmlFor="company">Company name <span className="req">*</span></label>
        <input
          id="company" type="text" required
          value={companyName} onChange={(e) => setCompanyName(e.target.value)}
        />

        <fieldset style={{ marginTop: 14, padding: 0, border: 0 }}>
          <legend style={{ padding: 0, fontWeight: 600, marginBottom: 6 }}>
            How will you use Giraops? <span className="req">*</span>
          </legend>
          <label style={modeOpt(operatingMode === 'service')}>
            <input
              type="radio" name="operatingMode" value="service"
              checked={operatingMode === 'service'}
              onChange={() => setOperatingMode('service')}
            />
            <div>
              <div style={{ fontWeight: 600 }}>We're a service business</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                Our techs respond to calls from our customers.
              </div>
            </div>
          </label>
          <label style={modeOpt(operatingMode === 'internal')}>
            <input
              type="radio" name="operatingMode" value="internal"
              checked={operatingMode === 'internal'}
              onChange={() => setOperatingMode('internal')}
            />
            <div>
              <div style={{ fontWeight: 600 }}>We're an internal operation</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                Our staff report breakdowns; our techs (or contractors) respond.
              </div>
            </div>
          </label>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            You can change this later in Settings.
          </div>
        </fieldset>

        <label htmlFor="country">Country <span className="req">*</span></label>
        <select
          id="country" required
          value={country} onChange={(e) => setCountry(e.target.value)}
        >
          {SUPPORTED_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>

        <label htmlFor="name">Your full name <span className="req">*</span></label>
        <input
          id="name" type="text" autoComplete="name" required
          value={fullName} onChange={(e) => setFullName(e.target.value)}
        />

        <label htmlFor="email">Work email <span className="req">*</span></label>
        <input
          id="email" type="email" autoComplete="email" required
          value={email} onChange={(e) => setEmail(e.target.value)}
        />

        <label htmlFor="password">Password (8+ characters) <span className="req">*</span></label>
        <input
          id="password" type="password" autoComplete="new-password" minLength={8} required
          value={password} onChange={(e) => setPassword(e.target.value)}
        />

        {/* Task #106 — optional AI add-on at signup. */}
        <label
          htmlFor="ai-addon"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: 12, marginTop: 12,
            border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            background: aiAddonEnabled ? 'rgba(139, 92, 246, 0.12)' : 'transparent',
            cursor: 'pointer',
          }}
        >
          <input
            id="ai-addon" type="checkbox"
            checked={aiAddonEnabled}
            onChange={(e) => setAiAddonEnabled(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ fontSize: 13, lineHeight: 1.4 }}>
            <strong>Add AI to my account (+$15/month).</strong> Customers go through
            a quick AI triage chat before dispatch, and your techs get an in-app AI
            assistant grounded in your manuals. You can turn this on or off any
            time from Settings — no commitment.
          </span>
        </label>

        <button type="submit" disabled={busy}>{busy ? 'Creating account...' : 'Create account'}</button>
        {error && <div className="error">{error}</div>}

        <div className="footer">
          Already have an account? <Link to="/login" className="auth-link">Sign in</Link>
        </div>
      </form>
    </div>
  );
}

const modeOpt = (selected) => ({
  display: 'flex', alignItems: 'flex-start', gap: 10,
  padding: 10, marginTop: 8, borderRadius: 8,
  border: `1px solid ${selected ? 'var(--accent, #ea580c)' : 'var(--border, #cbd5e1)'}`,
  background: selected ? 'rgba(234, 88, 12, 0.06)' : 'transparent',
  cursor: 'pointer',
});
